import { useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { stateOptions } from '@frontend/geography';
import { theme } from '@frontend/theme';
import {
  CustomChangeEvent,
  DateField,
  DropdownField,
  FieldChangeEvent,
  Heading,
  Text,
  currency,
  normalizeChangeHandler,
  useForm,
} from '@frontend/design-system';
import { ID_TYPE_OPTIONS } from '../../../constants/form-options';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { AccountDetailsCard } from '../atoms/display/account-details-card';
import { CollectBaseStep } from '../atoms/step/collect-base-step';
import { CollectStepPerformanceWrapper } from '../atoms/step/collect-step-performance-wrapper';
import { CareCreditCollectMultiSteps, CollectFormFieldNames } from '../collect-multi-step.types';
import { useCareCreditCollectContext } from '../context/care-credit-collect.context';
import { MIN_AMOUNT_FOR_OPTIONS } from '../utils/financing-options';

const CURRENT_STEP: CareCreditCollectMultiSteps = 'payment-method-form';
const PREV_STEP: CareCreditCollectMultiSteps = 'select-payment-method';
const NEXT_STEP_FINANCING: CareCreditCollectMultiSteps = 'select-financing-option';
const NEXT_STEP_RECEIPT: CareCreditCollectMultiSteps = 'receipt-options';

type EditableCollectFormFieldNames = Exclude<CollectFormFieldNames, 'accountHolderName' | 'purchaseAmount'>;

const styles = {
  stepContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  purchaseAmount: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.25)};
    margin-bottom: ${theme.spacing(1)};
  `,
};

export const Step2PaymentMethodForm = () => {
  return (
    <CollectStepPerformanceWrapper step={CURRENT_STEP}>
      <Step2PaymentMethodFormBody />
    </CollectStepPerformanceWrapper>
  );
};
const Step2PaymentMethodFormBody = () => {
  const { t } = useCareCreditTranslations();

  const {
    multiStep: { goPrevStep, goNextStep },
    careCreditData,
    person,
    invoice,
    collectForm,
    setSelectedFinanceOption,
    financeOptions,
    onChangeFormField,
  } = useCareCreditCollectContext();

  const personName = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim();

  const initialPurchaseAmount = String(invoice ? invoice.billedAmount / 100 : 0);
  const formattedPurchaseAmount = currency(Number(initialPurchaseAmount), { decimals: 2 });

  const todaysDate = dayjs().format('MM/DD/YYYY');

  const { isComplete, getFieldProps } = useForm({
    fields: {
      serviceCompletionDate: {
        type: 'date',
        required: true,
        value: collectForm.fields.serviceCompletionDate || todaysDate,
        validator: (field) => {
          if (!field.value) return t('Service completion date is required');

          const selectedDate = dayjs(field.value);
          if (selectedDate.isAfter(dayjs(), 'day')) {
            return t('Service completion date cannot be in the future');
          }
          return '';
        },
      },
      primaryIdType: {
        type: 'dropdown',
        required: true,
        value: collectForm.fields.primaryIdType || "DRIVER'S_LICENSE",
      },
      issuingState: { type: 'dropdown', required: true, value: collectForm.fields.issuingState || '' },
      expirationDate: {
        type: 'date',
        required: true,
        value: collectForm.fields.expirationDate || '',
        validator: (field) => {
          if (!field.value) return t('Expiration date is required');

          const selectedDate = dayjs(field.value);
          if (selectedDate.isBefore(dayjs().add(1, 'day'), 'day')) {
            return t('Expiration date must be in the future');
          }
          return '';
        },
      },
    },
  });

  const amountInDollars = Number(initialPurchaseAmount);

  const onClickNext = useCallback(() => {
    /**
     * CareCredit's alternative promos are shown for < 200 not <= 200.
     */
    if (amountInDollars < MIN_AMOUNT_FOR_OPTIONS) {
      /**
       * Select first option, if exists. Since we're skipping the promo select step.
       */
      const firstOption = financeOptions?.[0];
      if (firstOption) {
        setSelectedFinanceOption(firstOption);
      }

      goNextStep({
        orStep: NEXT_STEP_RECEIPT,
      });
    } else {
      goNextStep({
        orStep: NEXT_STEP_FINANCING,
      });
    }
  }, [goNextStep, amountInDollars, financeOptions, setSelectedFinanceOption]);

  const onClickBack = useCallback(() => {
    goPrevStep({
      orStep: PREV_STEP,
    });
  }, [goPrevStep]);

  const onAnyFieldChange = (e?: FieldChangeEvent) => {
    if (e) {
      normalizeChangeHandler(({ name, value }) => {
        const fieldName = name as EditableCollectFormFieldNames;
        const fieldProps = getFieldProps(fieldName);
        fieldProps.onChange(e);
        onChangeFormField(fieldName, value, isComplete);
      })(e as CustomChangeEvent);
    }
  };

  return (
    <CollectBaseStep
      id={CURRENT_STEP}
      label={t('Submit In-Office')}
      footer={{
        primary: {
          children: t('Next'),
          disabled: !isComplete,
        },
        onClickPrimary: onClickNext,

        back: {
          children: t('Back'),
          iconName: 'back',
        },
        showBackButton: true,
        onClickBack,
      }}
    >
      <div className='step-content' css={styles.stepContent}>
        {careCreditData && <AccountDetailsCard careCreditData={careCreditData} accountHolderName={personName} />}

        <div className='payment-form' css={styles.form}>
          <Heading level={3}>{t('Transaction Details')}</Heading>

          <div css={styles.purchaseAmount}>
            <Text size='small'>{t('Purchase Amount')}</Text>
            <Text size='large'>{formattedPurchaseAmount}</Text>
          </div>

          <DateField
            {...getFieldProps('serviceCompletionDate')}
            label={t('Service Completion Date')}
            onChange={onAnyFieldChange}
          />

          <Heading level={3}>{t('Customer ID Verification')}</Heading>

          <DropdownField {...getFieldProps('primaryIdType')} label={t('Primary ID Type')} onChange={onAnyFieldChange}>
            {ID_TYPE_OPTIONS.map((option) => (
              <DropdownField.Option key={option.value} value={option.value}>
                {option.label}
              </DropdownField.Option>
            ))}
          </DropdownField>

          <DropdownField {...getFieldProps('issuingState')} label={t('Issuing State')} onChange={onAnyFieldChange}>
            {stateOptions.US.map((option) => (
              <DropdownField.Option key={option.value} value={option.value}>
                {option.label}
              </DropdownField.Option>
            ))}
          </DropdownField>

          <DateField {...getFieldProps('expirationDate')} label={t('Expiration Date')} onChange={onAnyFieldChange} />
        </div>
      </div>
    </CollectBaseStep>
  );
};
